import React, { useState, useEffect, useMemo } from "react";
import { message, Alert, Checkbox, Statistic, Row, Col } from "antd";
import CriticalActionModal from "./CriticalActionModal";
import { rollbackEcheancePayment } from "../api/subscriptions/subscriptionsRequest";

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)} DH`;

const detailTypeLabel = (t) =>
  ({
    echeance: "Échéance",
    registration_fee: "Frais d'inscription",
    reactivation_fee: "Frais de réactivation",
    macaron_fee: "Frais de macaron",
  }[t] || t);

/**
 * Suggestion 7 — Roll back (un-pay) a paid echeance.
 * Soft-deletes the WHOLE settling payment (échéance + any registration/reactivation fees),
 * reverts the echeance to pending, and deducts the full payment amount from the agent.
 *
 * `paymentAmount` is the full settling payment total; `breakdown` lists all detail lines
 * being removed; `agentBalance` enables the live projection.
 */
const RollbackEcheancePaymentModal = ({
  visible,
  onCancel,
  onSuccess,
  echeance,
  paymentAmount = null,
  agentBalance = null,
  breakdown = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [updateAgentSolde, setUpdateAgentSolde] = useState(true);

  useEffect(() => {
    if (visible) setUpdateAgentSolde(true);
  }, [visible]);

  const amount = Number(paymentAmount ?? echeance?.amount ?? 0);
  const hasBalance = agentBalance !== null && agentBalance !== undefined && !isNaN(Number(agentBalance));

  const projectedBalance = useMemo(() => {
    if (!hasBalance || !updateAgentSolde) return hasBalance ? Number(agentBalance) : null;
    return Number((Number(agentBalance) - amount).toFixed(2));
  }, [hasBalance, agentBalance, amount, updateAgentSolde]);

  const willGoNegative = updateAgentSolde && projectedBalance !== null && projectedBalance < 0;

  const handleConfirm = async (reason) => {
    if (!echeance) return;
    setLoading(true);
    try {
      const response = await rollbackEcheancePayment(echeance.id, {
        reason,
        update_agent_solde: updateAgentSolde,
      });
      if (response?.success) {
        message.success(response.message || "Paiement de l'échéance annulé avec succès");
        onSuccess?.(response.data);
      } else {
        message.error(response?.message || response?.error || "Erreur lors de l'annulation");
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'annulation du paiement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CriticalActionModal
      visible={visible}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      loading={loading}
      danger
      title="Annuler le paiement de l'échéance"
      okText="Annuler le paiement"
      confirmPhrase={echeance?.id ? `#${echeance.id}` : null}
      summaryItems={[
        { label: "Échéance", to: `#${echeance?.id ?? ""}` },
        { label: "Statut", from: "Payé", to: "En attente" },
        { label: "Montant du paiement", to: fmt(amount) },
      ]}
      warningBanner={
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Action irréversible côté métier"
            description="Le paiement complet de cette échéance (et tous ses frais associés) sera supprimé et l'échéance repassera en attente."
          />
          {Array.isArray(breakdown) && breakdown.length > 1 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 8 }}
              message="Ce paiement contient plusieurs lignes — toutes seront annulées"
              description={
                <div>
                  {breakdown.map((d, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{detailTypeLabel(d.type)}</span>
                      <span>{fmt(d.amount)}</span>
                    </div>
                  ))}
                </div>
              }
            />
          )}
          {willGoNegative && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message="Attention : solde agent négatif"
              description={`Cette action laissera l'agent avec un solde négatif de ${fmt(projectedBalance)}.`}
            />
          )}
        </>
      }
    >
      <div style={{ marginTop: 12 }}>
        <Checkbox checked={updateAgentSolde} onChange={(e) => setUpdateAgentSolde(e.target.checked)}>
          Ajuster le solde de l'agent (déduire {fmt(amount)})
        </Checkbox>

        {hasBalance ? (
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Statistic title="Solde actuel de l'agent" value={Number(agentBalance)} precision={2} suffix="DH" />
            </Col>
            <Col span={12}>
              <Statistic
                title="Solde après l'action"
                value={projectedBalance ?? Number(agentBalance)}
                precision={2}
                suffix="DH"
                valueStyle={{ color: willGoNegative ? "#cf1322" : undefined }}
              />
            </Col>
          </Row>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Solde de l'agent indisponible ; l'ajustement sera appliqué côté serveur si l'option est cochée."
          />
        )}
      </div>
    </CriticalActionModal>
  );
};

export default RollbackEcheancePaymentModal;
